import * as React from "react"

import { cn } from "../../lib/utils"

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement> & {
    scrollContainerRef?: React.Ref<HTMLDivElement>
  }
>(({ className, scrollContainerRef, ...props }, ref) => (
  <div className="relative w-full overflow-auto" ref={scrollContainerRef}>
    <table
      ref={ref}
      className={cn("w-full caption-bottom text-root-size-base", className)}
      {...props}
    />
  </div>
))
Table.displayName = "Table"

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn("[&_tr]:border-b-[--wb-table-row-border-default]", className)}
    {...props}
  />
))
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn("[&_tr:last-child]:border-0", className)} {...props} />
))
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t-[--wb-table-row-border-default] bg-[--wb-table-header-background-default] font-root-text-weight-heavy [&>tr]:last:border-b-0",
      className
    )}
    {...props}
  />
))
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        "border-b-[--wb-table-row-border-default] transition-colors group bg-[--wb-table-row-background-default] hover:bg-[--wb-table-row-background-hover] data-[state=selected]:bg-[--wb-table-row-background-selected-default] group-hover:data-[state=selected]:bg-[--wb-table-row-background-selected-hover] data-[state=selected]:border-b-[--wb-table-row-border-selected-default] group-hover:data-[state=selected]:border-b-[--wb-table-row-border-selected-hover]",
        className
      )}
      {...props}
    />
  )
)
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "relative h-12 px-1 py-4 font-root-text-weight-heavy text-root-size-base text-root-text-color-subtle bg-[--wb-table-header-background-default] text-left align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-p5 font-500 leading-16",
      className
    )}
    {...props}
  />
))
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "p-1 text-root-size-base leading-4 text-root-text-color-base bg-[--wb-table-row-background-default] align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-p5 shadow-[inset_0px_-1px_0px_0px_var(--wb-table-row-border-default)]",
      "group-hover:bg-[--wb-table-cell-alternate-background-color]",
      "group-data-[state=selected]:bg-[--wb-table-row-background-selected-default] group-data-[state=selected]:shadow-[inset_0px_-1px_0px_0px_var(--wb-table-row-border-selected-default)]",
      "group-hover:group-data-[state=selected]:bg-[#FFE8D1] group-hover:group-data-[state=selected]:shadow-[inset_0px_-1px_0px_0px_var(--wb-table-row-border-selected-hover)]", // TODO: Table semantinc has opacity, causes transparency with sticky elements
      className
    )}
    {...props}
  />
))
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-root-size-base text-root-text-color-muted", className)}
    {...props}
  />
))
TableCaption.displayName = "TableCaption"

export { Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow }
